import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import { SxProps } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import React, { useCallback, useMemo, useState } from 'react';
import { Typography } from '@mui/material';
import { NameIdObj } from 'common/models/GenericTypes';
import { ClientsLapsedFilter } from 'common/models/Reporting/AnalyticsFilterData';
import MultipleDivisionsPicker from 'components/Pickers/MultipleDivisionPicker';
import ClientsGridComponent from 'components/Grids/ClientsGrid';
import UserPicker from 'components/Pickers/UserPicker';

interface Props {
  description: string;
  loadingHandler?: (isLoading: boolean) => void;
  errorHandler?: (message: string) => void;
}

const filterElementStyle: SxProps = { flex: '1 1 0' };
const allExtraOption: NameIdObj[] = [{ id: 0, name: 'All' }];

export default function LapsedClientsReport({ description, loadingHandler, errorHandler }: Props) {
  const [type, setType] = useState(4);
  const [timeRange, setTimeRange] = useState(3);
  const [ownerId, setOwnerId] = useState(0);
  const [selectedDivisions, setSelectedDivisions] = useState<number[]>([0]);
  const [filterData, setFilterData] = useState<ClientsLapsedFilter>();
  const [showGrid, setShowGrid] = useState(false);

  const runReportCallback = useCallback(async () => {
    const divs = selectedDivisions.includes(0) ? [] : selectedDivisions;
    const f: ClientsLapsedFilter = {
      ownerId: ownerId,
      divisions: divs,
      type: type,
      monthsAgo: timeRange,
    };

    setFilterData(f);
    setShowGrid(true);
  }, [selectedDivisions, ownerId, type, timeRange]);

  const onOwnerChange = useCallback((u: NameIdObj | null) => {
    setOwnerId(u ? u.id : 0);
  }, []);

  const onDivisionsChange = useCallback((divisions: NameIdObj[]) => {
    setSelectedDivisions(divisions.map((d) => d.id));
  }, []);

  const timeRangeLabel = useMemo(() => {
    if (type === 4) return 'Time Since last Job';
    if (type === 5) return 'Time Since last Placement';
    return 'Time Since Last X';
  }, [type]);

  return (
    <>
      <Box display="flex" gap={1}>
        <UserPicker
          label="Owner"
          userId={ownerId}
          onSelect={onOwnerChange}
          appendToStart={allExtraOption}
          sx={filterElementStyle}
        />
        <MultipleDivisionsPicker
          label="Division"
          value={selectedDivisions}
          onSelect={onDivisionsChange}
          appendToStart={allExtraOption}
          sx={filterElementStyle}
        />
        <TextField
          select
          label="Type"
          value={type.toString()}
          onChange={({ target }) => setType(+target.value)}
          sx={filterElementStyle}
        >
          <MenuItem value="0"></MenuItem>
          <MenuItem value="4">Jobs</MenuItem>
          <MenuItem value="5">Placements</MenuItem>
        </TextField>
        <TextField
          select
          label={timeRangeLabel}
          value={timeRange.toString()}
          onChange={({ target }) => setTimeRange(+target.value)}
          sx={filterElementStyle}
        >
          <MenuItem value="3">3 months</MenuItem>
          <MenuItem value="6">6 months</MenuItem>
          <MenuItem value="12">12 months</MenuItem>
          <MenuItem value="18">18 months</MenuItem>
          <MenuItem value="24">24 months</MenuItem>
          <MenuItem value="36">36 months</MenuItem>
        </TextField>
        <Button variant="contained" color="success" onClick={runReportCallback}>
          Run Report
        </Button>
      </Box>
      <Typography component="div" variant="h6" mt={2}>
        Report Description
      </Typography>
      <Typography component="div" variant="body2">
        {description}
      </Typography>
      <Box display={showGrid ? 'flex' : 'none'} flexDirection="column" flexGrow={1}>
        <ClientsGridComponent
          gridName="clients-lapsed-report-grid"
          source="lapsed-report"
          filterData={filterData}
          loadingHandler={loadingHandler}
          errorHandler={errorHandler}
        />
      </Box>
    </>
  );
}
